import {
  DashboardConfig,
  maximumScoreValue,
  minimumScoreValue,
  ScoreConfig,
  ScoreRange,
  ShareConfig,
} from '@algoan/dashboard/model';
import { isWidgetVisible, monitoring } from '@algoan/dashboard/utils';

export const isScoreConfigValid = (config: DashboardConfig): boolean => {
  if (!isWidgetVisible('score', config.widgets)) {
    return true;
  }
  const scoreConfig = config.scoreConfig;
  if (!scoreConfig) {
    return false;
  }

  const sortedRanges = [...scoreConfig.ranges].sort((range1, range2) => range1.min - range2.min);

  if (sortedRanges.length === 0) {
    return false;
  }

  const isIntersectionEmpty = (ranges: ScoreRange[]): boolean => {
    if (ranges.length <= 1) {
      return true;
    }

    const [firstRange, secondRange, ...otherRanges] = ranges;
    if (firstRange.max >= secondRange.min) {
      return false;
    } else {
      return isIntersectionEmpty([secondRange, ...otherRanges]);
    }
  };

  const doesConfigCoverAllValues = (): boolean => {
    const total = sortedRanges.reduce((total, range) => total + range.max - range.min + 1, 0);
    return total === maximumScoreValue + 1;
  };

  const firstRange = sortedRanges[0];
  const lastRange = sortedRanges[sortedRanges.length - 1];
  return (
    firstRange.min === minimumScoreValue &&
    lastRange.max === maximumScoreValue &&
    isIntersectionEmpty(sortedRanges) &&
    doesConfigCoverAllValues()
  );
};

export const findScoreRange = (scoreValue: number, scoreConfig: ScoreConfig): ScoreRange | undefined =>
  scoreConfig.ranges.find((range) => scoreValue >= range.min && scoreValue <= range.max);

export const isShareConfigValid = (shareConfig: ShareConfig | undefined): boolean => {
  if (shareConfig === undefined) {
    return true;
  }

  if (!shareConfig.recipients) {
    monitoring.logError({
      fingerprint: 'share_config_has_no_recipients',
      message: 'Share config does not have any recipients',
      context: { level: 'error' },
    });
    return false;
  }

  const configValid =
    shareConfig.recipients.every((recipient) => recipient.projectId) &&
    shareConfig.recipients.every((recipient) => recipient.name) &&
    shareConfig.recipients.every((recipient) => recipient.organizationId);

  if (!configValid) {
    monitoring.logError({
      fingerprint: 'invalid_share_config',
      message: 'Share config is invalid',
      context: { level: 'error' },
    });
    return false;
  } else {
    return true;
  }
};
